import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import accelaSearchLogo from "@/assets/icons/as-logo-complete.svg";
import { useAppSelector, useAppStore } from "@/app/hooks";
import { resolveRoute } from "@/app/routes";
import { AppShell, PageHeader, Sidebar, Topbar, Void } from "@/shared/layout";
import { Plus } from "@/shared/ui/icons";
import { AccountMenu } from "./AccountMenu";
import { AppFooter } from "./AppFooter";
import { ShellDialogsProvider } from "./dialogs";
import {
  selectCurrentConfiguration,
  selectDisplayedShops,
  selectEditorHasChanges,
  selectHistoryChanges,
  selectPendingShops,
} from "./legacySelectors";
import { NAVIGATION_GROUPS, SAVE_HIDDEN_GROUPS, SHOP_INDEPENDENT_GROUPS } from "./navigation";
import { NotificationDetailModal } from "./notifications/NotificationDetailModal";
import { NotificationsModal } from "./notifications/NotificationsModal";
import { NotificationsBell } from "./NotificationsBell";
import { SaveChangesBar } from "./save/SaveChangesBar";
import { SidebarBrand } from "./SidebarBrand";
import { SidebarNavigation } from "./SidebarNavigation";
import { TermsModal } from "./TermsModal";
import { UnsavedChangesModal } from "./UnsavedChangesModal";
import { UsageRecap } from "./UsageRecap";
import { usePageCopy } from "./usePageCopy";
import { useUnsavedChanges } from "./useUnsavedChanges";
import { WorkspacePicker } from "./WorkspacePicker";

const COLLAPSED_KEY = "as-sidebar-collapsed";

export function AppFrame() {
  const { t } = useTranslation();
  const location = useLocation();
  const store = useAppStore();
  const shops = useAppSelector(selectDisplayedShops);
  const pendingShops = useAppSelector(selectPendingShops);
  const current = useAppSelector(selectCurrentConfiguration);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === "true");
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  // Cambio pagina: i due menu si chiudono. Si aggiusta lo stato durante il render (pattern React per lo
  // stato derivato da un valore precedente) invece di un effetto che chiamerebbe setState dopo il commit.
  const [menusPathname, setMenusPathname] = useState(location.pathname);
  if (menusPathname !== location.pathname) {
    setMenusPathname(location.pathname);
    setWorkspaceOpen(false);
    setAccountMenuOpen(false);
  }
  const guard = useUnsavedChanges();
  const route = resolveRoute(location.pathname);
  const { eyebrow, title, description } = usePageCopy(route);

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);
  useEffect(() => {
    // Stessi criteri delle modali di conferma: history di configurazione o un editor Visual Machine con modifiche reali.
    const warnOnExit = (event: BeforeUnloadEvent) => {
      const state = store.getState();
      if (selectHistoryChanges(state).length || selectEditorHasChanges(state).any) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warnOnExit);
    return () => window.removeEventListener("beforeunload", warnOnExit);
  }, [store]);

  const openWorkspace = useCallback((open: boolean) => {
    if (open) setAccountMenuOpen(false);
    setWorkspaceOpen(open);
  }, []);
  const openAccount = useCallback((open: boolean) => {
    if (open) setWorkspaceOpen(false);
    setAccountMenuOpen(open);
  }, []);

  const hasShops = shops.length > 0;
  const showNoShopVoid = !hasShops && !SHOP_INDEPENDENT_GROUPS.has(route.group);
  const visibleGroups = hasShops
    ? NAVIGATION_GROUPS
    : NAVIGATION_GROUPS.filter((group) => group.key === "administration");
  const showSave = hasShops && !SAVE_HIDDEN_GROUPS.has(route.group);

  return (
    <ShellDialogsProvider switchShop={guard.switchShop}>
      <AppShell collapsed={collapsed}>
        <Sidebar>
          <SidebarBrand
            hasShops={hasShops}
            collapsed={collapsed}
            onToggle={() => setCollapsed((value) => !value)}
          />
          <WorkspacePicker
            shops={shops}
            pendingShops={pendingShops}
            current={current}
            open={workspaceOpen}
            onOpenChange={openWorkspace}
            onSelect={(shop) => {
              setWorkspaceOpen(false);
              guard.switchShop(shop);
            }}
          />
          <SidebarNavigation groups={visibleGroups} collapsed={collapsed} />
          {hasShops && <UsageRecap />}
        </Sidebar>
        <div className="app-stage">
          <Topbar>
            <div className="topbar-context">
              {!showNoShopVoid && (
                <>
                  <span className="topbar-eyebrow">{eyebrow}</span>
                  <strong>{title}</strong>
                </>
              )}
            </div>
            <div className="topbar-actions">
              {showSave && <SaveChangesBar />}
              {hasShops && <NotificationsBell />}
              <AccountMenu open={accountMenuOpen} onOpenChange={openAccount} />
            </div>
          </Topbar>
          <main className="app-content" id="main-content">
            {showNoShopVoid ? (
              <Void
                image={accelaSearchLogo}
                title={t("shell.noShop.title")}
                description={t("shell.noShop.description")}
                action={
                  <NavLink to="/wizard" className="btn btn-primary">
                    <Plus aria-hidden="true" />
                    {t("shell.noShop.cta")}
                  </NavLink>
                }
              />
            ) : (
              <div className="dw-page">
                <PageHeader eyebrow={eyebrow} title={title} description={description} />
                <Outlet />
              </div>
            )}
          </main>
          <AppFooter onOpenTerms={() => setTermsOpen(true)} />
        </div>
        <UnsavedChangesModal
          open={guard.modalOpen}
          onCancel={guard.cancelExit}
          onExit={guard.exitWithoutSaving}
        />
        <NotificationsModal />
        <NotificationDetailModal />
        <TermsModal open={termsOpen} onOpenChange={setTermsOpen} />
      </AppShell>
    </ShellDialogsProvider>
  );
}
